const LCG_MULTIPLIER = 1664525;
const LCG_INCREMENT = 1013904223;
const DEFAULT_COUNT_PER_SIDE = 2;
const LOOP_MIN_OFFSET_FACTOR = -1 / 6;

export type SideElementsRng = {
	nextInt: (max: number) => number;
};

export const createRng = (seed: number): SideElementsRng => {
	let state = seed >>> 0;
	return {
		nextInt(max: number) {
			state = (state * LCG_MULTIPLIER + LCG_INCREMENT) >>> 0;
			return state % max;
		},
	};
};

export const normalizeCountPerSide = (count?: number): 1 | 2 => {
	const normalized = Math.max(1, Math.min(2, Number.isFinite(count) ? Number(count) : DEFAULT_COUNT_PER_SIDE));
	return normalized === 1 ? 1 : 2;
};

export type LaneGeometry = {
	loopMin: number;
	loopMax: number;
	loopSpan: number;
	viewHeight: number;
};

export const createLaneGeometry = ({ height, elementSize }: { height: number; elementSize: number }): LaneGeometry => {
	const loopMin = elementSize * LOOP_MIN_OFFSET_FACTOR;
	const loopMax = height + elementSize / 2;

	return {
		loopMin,
		loopMax,
		loopSpan: loopMax - loopMin,
		viewHeight: height,
	};
};

export type SideElementItem = {
	id: string;
	y: number;
	xSide: 'left' | 'right';
	jitter: number;
	image: string;
};

export const createInitialItems = ({
	sides,
	geometry,
	itemsPerSide,
	elementSize,
	rng,
	sprites,
}: {
	sides: 'left' | 'right' | 'both';
	geometry: LaneGeometry;
	itemsPerSide: 1 | 2;
	elementSize: number;
	rng: SideElementsRng;
	sprites?: string[];
}): SideElementItem[] => {
	const items: SideElementItem[] = [];
	const gap = (geometry.viewHeight + elementSize) / itemsPerSide;
	const sideAnchors: Record<'left' | 'right', number> = {
		left: -elementSize / 2, // left bank should emerge from the top
		right: geometry.viewHeight / 2, // right bank starts midway down the lane
	};
	const spritePool = sprites?.length ? sprites : [];
	const defaultSprite = spritePool[0] ?? '';
	const buildSpriteQueue = (): string[] => {
		if (!spritePool.length) return [defaultSprite];
		const pool = [...spritePool];
		for (let i = pool.length - 1; i > 0; i -= 1) {
			const j = rng.nextInt(i + 1);
			const tmp = pool[i];
			pool[i] = pool[j];
			pool[j] = tmp;
		}
		return pool;
	};

	let spriteQueue = buildSpriteQueue();
	const drawSprite = (exclude?: string): string => {
		if (!spritePool.length) return defaultSprite;
		let attempts = 0;
		while (attempts < Math.max(1, spritePool.length)) {
			if (!spriteQueue.length) {
				spriteQueue = buildSpriteQueue();
			}
			const sprite = spriteQueue.shift() ?? defaultSprite;
			if (sprite !== exclude || attempts === spritePool.length - 1) {
				return sprite;
			}
			spriteQueue.push(sprite);
			attempts++;
		}
		return defaultSprite;
	};

	const addSide = (sideKey: 'left' | 'right') => {
		const anchorY = sideAnchors[sideKey];
		const phaseOffset = sideKey === 'right' ? gap / 2 : 0; // stagger right bank so sides alternate in view
		let lastSprite: string | undefined;
		for (let i = 0; i < itemsPerSide; i++) {
			const image = drawSprite(lastSprite);
			lastSprite = image;
			items.push({
				id: `${sideKey}-${i}`,
				y: anchorY + phaseOffset + i * gap,
				xSide: sideKey,
				jitter: rng.nextInt(6001) / 1000 - 3,
				image,
			});
		}
	};
	if (sides === 'left' || sides === 'both') addSide('left');
	if (sides === 'right' || sides === 'both') addSide('right');
	return items;
};

const pickSpriteForWrap = (current: string, used: Set<string>, rng: SideElementsRng, sprites: string[]): string => {
	if (!sprites.length) return current;
	const allowReuse = used.size >= sprites.length;
	let candidateIndex = rng.nextInt(sprites.length);
	let attempts = 0;
	while (attempts < sprites.length) {
		const candidate = sprites[candidateIndex];
		const available = allowReuse || !used.has(candidate);
		if (candidate !== current && available) {
			return candidate;
		}
		candidateIndex = (candidateIndex + 1) % sprites.length;
		attempts++;
	}
	return sprites[candidateIndex];
};

export const wrapAndAdvanceItems = ({
	items,
	motionScale,
	motionDirection,
	baseSpeedPxPerSec,
	deltaTime,
	rng,
	loopMin,
	loopMax,
	loopSpan,
	sprites,
}: {
	items: SideElementItem[];
	motionScale: number;
	motionDirection: 1 | -1;
	baseSpeedPxPerSec: number;
	deltaTime: number;
	rng: SideElementsRng;
	loopMin: number;
	loopMax: number;
	loopSpan: number;
	sprites?: string[];
}): SideElementItem[] => {
	const spritePool = sprites?.length ? sprites : [];
	const speed = baseSpeedPxPerSec * Math.max(0, motionScale);
	const dir = motionDirection === -1 ? -1 : 1;
	const dy = speed * deltaTime * dir;
	const next: SideElementItem[] = [];
	items.forEach((item, idx) => {
		let y = item.y + dy;
		let wrapped = false;
		if (y < loopMin) {
			y += loopSpan;
			wrapped = true;
		} else if (y > loopMax) {
			y -= loopSpan;
			wrapped = true;
		}
		let image = item.image;
		if (wrapped) {
			// Only track sprites used on the SAME side to avoid repetition
			const used = new Set<string>();
			next.forEach((assigned) => {
				if (assigned.xSide === item.xSide) {
					used.add(assigned.image);
				}
			});
			for (let i = idx + 1; i < items.length; i += 1) {
				if (items[i].xSide === item.xSide) {
					used.add(items[i].image);
				}
			}
			image = pickSpriteForWrap(image, used, rng, spritePool);
		}
		next.push({ ...item, y, image });
	});
	return next;
};
